/**
 * Client-side auth. SPEC-0003 §1.
 *
 * NOTHING here is a security boundary. The gate is the API's server-side token
 * verification plus `orgId` claim check; this only decides what to render and
 * what token to attach. Bypassing this file entirely still yields 401 or 403.
 *
 * No registration flow is exposed, deliberately. Self-registration is ruled
 * out, not deferred — invite-only is the permanent primary path (ADR-0012
 * addendum). The UI offers sign-in and password reset only.
 */
import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { setTokenProvider } from './api';
import {
	GoogleAuthProvider,
	getAuth,
	onAuthStateChanged,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
	signInWithPopup,
	signOut,
	type Auth,
	type User,
} from 'firebase/auth';

const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * When the API runs with `PICKLII_AUTH_DISABLED=true`, the front end has no
 * Firebase project to talk to either. Local development only.
 */
export const authDisabled = import.meta.env.VITE_AUTH_DISABLED === 'true';

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

function getAuthInstance(): Auth {
	if (auth) return auth;
	app ??= initializeApp(firebaseConfig);
	auth = getAuth(app);
	return auth;
}

/** Generic on purpose: sign-in failures must be indistinguishable. §4. */
const GENERIC_SIGNIN_ERROR = 'That email and password combination did not work.';

interface AuthState {
	user: { uid: string; email: string } | null;
	loading: boolean;
	signInWithGoogle(): Promise<void>;
	signInWithPassword(email: string, password: string): Promise<void>;
	resetPassword(email: string): Promise<void>;
	signOutNow(): Promise<void>;
	getToken(): Promise<string | null>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
	const [user, setUser] = useState<AuthState['user']>(
		authDisabled ? { uid: 'dev-user', email: 'dev@localhost' } : null,
	);
	const [loading, setLoading] = useState(!authDisabled);

	useEffect(() => {
		if (authDisabled) return;
		return onAuthStateChanged(getAuthInstance(), (next: User | null) => {
			setUser(next && next.email ? { uid: next.uid, email: next.email } : null);
			setLoading(false);
		});
	}, []);

	const value = useMemo<AuthState>(
		() => ({
			user,
			loading,
			async signInWithGoogle() {
				await signInWithPopup(getAuthInstance(), new GoogleAuthProvider());
			},
			async signInWithPassword(email, password) {
				try {
					await signInWithEmailAndPassword(getAuthInstance(), email, password);
				} catch {
					// Never leak which addresses have accounts. Email enumeration
					// protection is on server-side; this keeps the UI consistent with it.
					throw new Error(GENERIC_SIGNIN_ERROR);
				}
			},
			async resetPassword(email) {
				// Always reports success, whether or not the address exists.
				try {
					await sendPasswordResetEmail(getAuthInstance(), email);
				} catch {
					/* swallowed deliberately — enumeration protection */
				}
			},
			async signOutNow() {
				if (!authDisabled) await signOut(getAuthInstance());
			},
			async getToken() {
				if (authDisabled) return null;
				return (await getAuthInstance().currentUser?.getIdToken()) ?? null;
			},
		}),
		[user, loading],
	);

	// Installed during render, not in an effect. React runs child effects before
	// parent effects, so a page that fetches in its own effect would otherwise
	// issue its first request before the provider was in place — and arrive
	// without an Authorization header.
	setTokenProvider(value.getToken);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
	const context = useContext(AuthContext);
	if (context === null) throw new Error('useAuth must be used inside <AuthProvider>');
	return context;
}
